/**
 * Iter XX.84 — LiveBreakthroughToast.
 *
 * A tiny ambient pill that fades in at the top of the landing,
 * holds for ~2.5s, and fades out. Zero motion beyond opacity —
 * no slide-in, no bounce, no shadow pulse. It must feel like a
 * quiet status line, not a marketing pop-up.
 *
 * Never blocks interaction (pointer-events: none).
 */
import React, { useEffect, useState } from "react";
import { trackOnce } from "../lib/claritySession";

export default function LiveBreakthroughToast({
  message, visible, fadeMs = 250, onDone, testid = "live-breakthrough-toast",
}) {
  // We keep the DOM node while `visible` is true; then wait for the
  // fade-out to finish before unmounting so the animation plays.
  const [mounted, setMounted] = useState(false);
  const [opaque, setOpaque] = useState(false);

  useEffect(() => {
    if (visible && message) {
      setMounted(true);
      // Allow one paint before flipping opacity for the transition
      // to trigger.
      const t = requestAnimationFrame(() => {
        setOpaque(true);
        // Iter XX.87 — Fire `live_breakthrough_viewed` ONLY when the
        // pill actually paints. This is a stricter definition than
        // the older `live_breakthrough_shown` event (which fires the
        // moment the toast is scheduled — before the DOM node exists).
        // Both events remain wired: `_shown` for the funnel count and
        // `_viewed` for the visibility-confirmed impression.
        try {
          trackOnce("live_breakthrough_viewed", {
            category: message?.category || "unknown",
            variant: message?.variant || "unknown",
          }, `live_breakthrough_viewed:${message?.variant || "x"}`);
        } catch { /* noop */ }
      });
      return () => cancelAnimationFrame(t);
    }
    if (!visible && mounted) {
      setOpaque(false);
      const timer = setTimeout(() => {
        setMounted(false);
        if (onDone) onDone();
      }, fadeMs);
      return () => clearTimeout(timer);
    }
     
  }, [visible, message, fadeMs]);

  if (!mounted || !message) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-3 z-40 flex justify-center px-4"
      data-testid={testid}
    >
      <div
        data-testid={`${testid}-pill`}
        className={
          "max-w-md w-full sm:w-auto text-center text-[13px] leading-snug "
          + "rounded-full border border-neutral-800 bg-neutral-900/85 backdrop-blur "
          + "px-4 py-2 text-neutral-200 shadow-sm "
          + "transition-opacity ease-out"
        }
        style={{
          transitionDuration: `${fadeMs}ms`,
          opacity: opaque ? 1 : 0,
        }}
      >
        {message.text}
      </div>
    </div>
  );
}
